import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { ScoreBadge } from "@/components/app/ScoreBadge";
import {
  Card, CardContent, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Search, Layers, AlertTriangle, Target, Sparkles, TrendingDown } from "lucide-react";
import { InfoTip } from "@/components/app/InfoTip";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const COLORS = ["#0f172a", "#10b981", "#f97316", "#3b82f6", "#a855f7", "#ef4444", "#14b8a6", "#eab308", "#6366f1", "#ec4899"];

function KpiCard({ icon: Icon, label, value, sub, tone = "slate", testid, term }) {
  const tones = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    blue: "text-blue-700",
    violet: "text-violet-700",
    orange: "text-orange-700",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1">
          {label}
          {term ? <InfoTip term={term} /> : null}
        </span>
        <Icon strokeWidth={1.5} className="h-4 w-4 text-slate-400" />
      </div>
      <div className={`mt-2 text-3xl font-bold tracking-tight ${tones[tone]}`}>{value}</div>
      {sub ? <div className="mt-1 text-xs text-slate-500">{sub}</div> : null}
    </div>
  );
}

export default function Dashboard() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wasted, setWasted] = useState(null);
  const [coverage, setCoverage] = useState(null);

  useEffect(() => {
    if (!selectedBrandId) return;
    setLoading(true);
    api.get(`/dashboard/summary?brand_id=${selectedBrandId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    // Closed-loop: pull wasted-spend KPI in parallel; fall back silently when
    // the brand has never uploaded an ads report.
    api.get(`/ads/wasted-spend?brand_id=${selectedBrandId}&weeks=4`)
      .then((r) => setWasted(r.data))
      .catch(() => setWasted(null));
    // Data Flywheel: repo vs seed vs unclassified coverage for the latest
    // upload. Silently absent for brands with no uploads yet.
    api.get(`/admin/clusters/coverage?brand_id=${selectedBrandId}`)
      .then((r) => setCoverage(r.data))
      .catch(() => setCoverage(null));
  }, [selectedBrandId]);

  if (!selectedBrandId) {
    return (
      <div className="text-sm text-slate-500">Select a brand from the top bar to view its dashboard.</div>
    );
  }

  const k = data?.kpis;
  const hasData = data?.latest_upload;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Dashboard</p>
          <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            {selectedBrand?.brand_name || "Brand"} <span className="text-slate-400 font-medium">overview</span>
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            {hasData
              ? `Latest upload: ${data.latest_upload.file_name} · ${data.latest_upload.week_start_date} → ${data.latest_upload.week_end_date}`
              : "No weekly upload yet — upload your first Top Search Terms CSV to activate insights."}
          </p>
        </div>
        <Link to="/upload">
          <Button data-testid="dashboard-cta-upload" className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg h-10">
            Upload weekly CSV
          </Button>
        </Link>
      </div>

      {!hasData ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center">
          <Sparkles className="h-6 w-6 text-slate-400 mx-auto" />
          <h3 className="mt-3 text-lg font-semibold text-slate-900">Ready when you are</h3>
          <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
            Upload your Amazon Top Search Terms CSV. We will normalize, cluster, score & generate weekly recommendations automatically.
          </p>
          <Link to="/upload">
            <Button className="mt-5 bg-slate-900 text-white hover:bg-slate-800 rounded-lg">Get started</Button>
          </Link>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <KpiCard
              testid="kpi-total-keywords"
              icon={Search}
              label="Keywords this week"
              value={k.total_keywords?.toLocaleString() || 0}
              sub={data.latest_upload?.week_start_date && data.latest_upload?.week_end_date
                ? `${data.latest_upload.week_start_date} → ${data.latest_upload.week_end_date} · ${k.new_keywords || 0} new`
                : `${k.new_keywords || 0} new this week`}
              term="NEW_KW"
            />
            <KpiCard
              testid="kpi-alltime-keywords"
              icon={Search}
              tone="violet"
              label="All-time keywords"
              value={k.total_keywords_alltime?.toLocaleString() || 0}
              sub={`distinct terms · across ${k.total_uploads || 0} weekly upload${k.total_uploads === 1 ? "" : "s"}`}
            />
            <KpiCard testid="kpi-high-opportunity" icon={TrendingUp} tone="emerald" label="High-opportunity" value={k.high_opportunity_keywords} sub="Score ≥ 70" term="OPPORTUNITY" />
            <KpiCard testid="kpi-rising" icon={Layers} tone="blue" label="Rising keywords" value={k.rising_keywords} sub="Significant rank gain" term="RISING" />
            <KpiCard testid="kpi-gaps" icon={AlertTriangle} tone="rose" label="Product gaps" value={k.product_gaps} sub="High demand, weak SKU fit" term="PRODUCT_GAP" />
          </div>

          {wasted && wasted.weeks_covered > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="dashboard-closed-loop">
              <KpiCard
                testid="kpi-wasted-spend"
                icon={TrendingDown}
                tone="rose"
                label="Wasted ad spend (4w)"
                value={`₹${Number(wasted.wasted_spend || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                sub={`${wasted.wasted_clicks?.toLocaleString() || 0} clicks · 0 orders · ${wasted.wasted_terms} term${wasted.wasted_terms === 1 ? "" : "s"}`}
                term="WASTED_SPEND"
              />
              <KpiCard
                testid="kpi-overall-acos"
                icon={Target}
                tone={wasted.overall_acos == null ? "slate" : (wasted.overall_acos < 25 ? "emerald" : (wasted.overall_acos < 40 ? "orange" : "rose"))}
                label="Overall ACoS"
                value={wasted.overall_acos != null ? `${wasted.overall_acos}%` : "—"}
                sub={`across ${wasted.weeks_covered} week${wasted.weeks_covered === 1 ? "" : "s"} · spend ₹${Number(wasted.total_spend || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                term="ACOS"
              />
              <KpiCard
                testid="kpi-ad-sales"
                icon={TrendingUp}
                tone="emerald"
                label="Attributed ad sales (4w)"
                value={`₹${Number(wasted.total_sales || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`}
                sub="From the latest 4 ad-perf uploads"
                term="AD_PERF_UPLOAD"
              />
              <div data-testid="kpi-closed-loop-cta" className="bg-gradient-to-br from-slate-900 to-slate-700 rounded-xl p-5 shadow-sm text-white flex flex-col justify-between">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-300 inline-flex items-center gap-1">
                    Closed-loop learning <InfoTip term="CLOSED_LOOP" className="text-slate-400 hover:text-white" />
                  </div>
                  <div className="mt-2 text-sm">
                    Next reco regen will auto-add <span className="font-semibold">{wasted.wasted_terms} negative{wasted.wasted_terms === 1 ? "" : "s"}</span> from this data.
                  </div>
                </div>
                <Link to="/ads-performance" className="mt-3">
                  <Button data-testid="dashboard-cta-ads-perf" size="sm" className="w-full bg-white text-slate-900 hover:bg-slate-100">View / upload report</Button>
                </Link>
              </div>
            </div>
          )}

          {(!wasted || wasted.weeks_covered === 0) && (
            <div data-testid="dashboard-ads-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-5 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
                  <TrendingDown className="h-4 w-4 text-slate-500" /> Closed-loop learning is OFF
                  <InfoTip term="CLOSED_LOOP" />
                </div>
                <p className="mt-0.5 text-xs text-slate-500">Upload an Amazon Sponsored-Products / Search-Term report to mark wasteful terms as auto-negatives next week.</p>
              </div>
              <Link to="/ads-performance">
                <Button data-testid="dashboard-cta-enable-cl" variant="outline" size="sm">Upload ad report</Button>
              </Link>
            </div>
          )}

          {coverage && coverage.total > 0 && (
            <DataFlywheelCard coverage={coverage} />
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-semibold text-slate-900 tracking-tight">Top demand clusters</h3>
                <Link to="/clusters-dashboard" className="text-xs font-semibold text-slate-500 hover:text-slate-900">View all →</Link>
              </div>
              <div className="h-72" style={{ minHeight: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.cluster_distribution} layout="vertical" margin={{ left: 30 }}>
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                    <YAxis dataKey="cluster_name" type="category" stroke="#94a3b8" fontSize={11} width={130} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#0f172a" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-base font-semibold text-slate-900 tracking-tight mb-3">Cluster mix</h3>
              <div className="h-72" style={{ minHeight: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={data.cluster_distribution} dataKey="count" nameKey="cluster_name" outerRadius={90} innerRadius={50}>
                      {(data.cluster_distribution || []).map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-900 tracking-tight">Top opportunity keywords</h3>
                <Link to="/search-terms" className="text-xs font-semibold text-slate-500 hover:text-slate-900">View all →</Link>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {(data.top_keywords || []).map((t) => (
                    <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-5 py-2.5 text-slate-700">{t.search_term}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{t.detected_cluster_name || "—"}</td>
                      <td className="px-5 py-2.5 text-right">
                        <ScoreBadge score={t.opportunity_score} label="OS" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-base font-semibold text-slate-900 tracking-tight">Product opportunity</h3>
                <Link to="/product-opportunity" className="text-xs font-semibold text-slate-500 hover:text-slate-900">View all →</Link>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {(data.product_opportunity || []).slice(0, 8).map((p) => (
                    <tr key={p.product_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-5 py-2.5 text-slate-700">{p.product_name}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{p.matched_keywords} kw</td>
                      <td className="px-5 py-2.5 text-right">
                        <ScoreBadge score={p.avg_opportunity} label="OS" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-slate-900 tracking-tight flex items-center gap-2">
                <Target strokeWidth={1.5} className="h-4 w-4" /> Top competitor ASINs
              </h3>
              <Link to="/competitors-intel" className="text-xs font-semibold text-slate-500 hover:text-slate-900">View intel →</Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {(data.competitor_frequency || []).slice(0, 10).map((c) => (
                <div key={c.asin} className="rounded-lg border border-slate-200 px-3 py-2 text-xs">
                  <div className="font-mono font-semibold text-slate-900">{c.asin}</div>
                  <div className="text-slate-500">appeared {c.count}×</div>
                </div>
              ))}
              {(!data.competitor_frequency || !data.competitor_frequency.length) && (
                <div className="text-sm text-slate-500">No competitor ASINs detected in this upload.</div>
              )}
            </div>
          </div>
        </>
      )}
      {loading && <div className="text-sm text-slate-500">Loading…</div>}
    </div>
  );
}

function DataFlywheelCard({ coverage }) {
  const total = coverage.total || 0;
  const repo = coverage.from_repository || 0;
  const seed = coverage.from_seed_detector || 0;
  const unc = coverage.unclassified || 0;
  const repoPct = total ? (repo / total) * 100 : 0;
  const seedPct = total ? (seed / total) * 100 : 0;
  const uncPct = total ? (unc / total) * 100 : 0;
  return (
    <div data-testid="dashboard-data-flywheel" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <Sparkles className="h-4 w-4 text-violet-500" />
            Data flywheel coverage
            <InfoTip term="DATA_FLYWHEEL" />
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            How this week's <span className="font-semibold text-slate-700">{total.toLocaleString()}</span> terms got their cluster.
            Higher repository share = the platform is learning from other brands.
          </p>
        </div>
        <div data-testid="flywheel-repo-pct" className="text-right">
          <div className="text-3xl font-bold text-violet-700">{coverage.repo_coverage_pct?.toFixed?.(1) ?? coverage.repo_coverage_pct}%</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">from repository</div>
        </div>
      </div>
      <div className="mt-4 flex h-2.5 w-full overflow-hidden rounded-full bg-slate-100" title={`Repository ${repo} · Seed ${seed} · Unclassified ${unc}`}>
        <div className="bg-violet-500" style={{ width: `${repoPct}%` }} />
        <div className="bg-emerald-500" style={{ width: `${seedPct}%` }} />
        <div className="bg-slate-300" style={{ width: `${uncPct}%` }} />
      </div>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
        <FlywheelLegend color="bg-violet-500" label="Repository" count={repo} total={total} testid="flywheel-legend-repo" />
        <FlywheelLegend color="bg-emerald-500" label="Seed clusters" count={seed} total={total} testid="flywheel-legend-seed" />
        <FlywheelLegend color="bg-slate-300" label="Unclassified" count={unc} total={total} testid="flywheel-legend-unc" />
      </div>
    </div>
  );
}

function FlywheelLegend({ color, label, count, total, testid }) {
  const pct = total ? ((count / total) * 100).toFixed(1) : "0.0";
  return (
    <div data-testid={testid} className="inline-flex items-center gap-2">
      <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
      <span className="text-slate-600">{label}</span>
      <span className="ml-auto font-mono font-semibold text-slate-900">{count.toLocaleString()}</span>
      <span className="text-slate-400">· {pct}%</span>
    </div>
  );
}
